import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import CartaoErro from '../../componentes/CartaoErro'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import type { PerfilImportacao, TabelaPreco, Cliente } from '../../db/tipos'
import { supabase } from '../../lib/supabase'
import { converterNumeroBR, normalizarTexto } from '../../lib/textoBR'
import { mapaInicial } from '../../importacao/mapeamento'
import {
  aplicarMapa, validarProdutos, validarClientes, converterComissao, converterQuantidadeRegra,
} from '../../importacao/validacao'
import type { ResultadoValidacao, LinhaMapeada } from '../../importacao/validacao'
import {
  validarAliquotas, detectarTipoDeAliquota, paraLinhaIcms, paraLinhaSt, paraLinhaDescontoFrete,
} from '../../importacao/validarAliquotas'
import { aplicarClientes } from '../../importacao/aplicarClientes'
import { CLASSIFICACOES, ROTULO_CLASSIFICACAO, type Classificacao } from '../../clientes/classificacao'
import {
  passoDeComissaoDaImportacao, linhasParaComissao, rotearComissoes,
} from '../../importacao/passoComissao'
import type { PendenteComissao } from '../../importacao/passoComissao'
import { politicaDaTabela } from '../../importacao/politicaComissao'
import type { Politica } from '../../importacao/politicaComissao'
import EscolhaPoliticaComissao from './EscolhaPoliticaComissao'
import {
  calcularDivergencias, precisaAbrirDivergencias, divergenciasSemHistorico,
} from '../../importacao/divergencias'
import type { Divergencias } from '../../importacao/divergencias'
import { camposCaixaFechada } from '../../importacao/payloadCaixaFechada'
import { camposProduto } from '../../importacao/payloadProduto'
import { pedeEscolhaDeAba, corpoImportarArquivo } from '../../importacao/abaDaPlanilha'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../../sync/outbox'
import { sincronizar } from '../../sync/sincronizador'
import CampoNomeDeTabela from './CampoNomeDeTabela'
import MapeamentoColunas from './MapeamentoColunas'
import PreviaImportacao from './PreviaImportacao'
import MesclagemDuplicados from './MesclagemDuplicados'
import EscolhaCondicao from './EscolhaCondicao'
import EscolhaAba from './EscolhaAba'
import { agruparCondicoes, colunasQueDiferem } from '../../importacao/condicoes'
import type { Agrupamento } from '../../importacao/condicoes'
import TelaDivergencias from './TelaDivergencias'
import PreenchimentoComissao from '../../componentes/PreenchimentoComissao'
import { escolherRegra, regraPropriaDaTabela, regraPadraoDaFornecedora } from '../../comissoes/motor'
import { produtosComPrecoNaRegra, produtosComPrecoNaTabela } from '../../comissoes/produtosVendaveis'
import { confirmar } from '../../componentes/confirmar'
import { preenchimentoDaUrl } from '../../importacao/preenchimentoDaUrl'

type Etapa =
  | 'escolha' | 'abas' | 'mapeamento' | 'condicoes' | 'previa' | 'divergencias' | 'politica' | 'comissoes'
  | 'aplicando' | 'resultado'
type Tipo = 'produtos' | 'clientes' | 'aliquotas'

function formatoDoNome(nome: string): 'xlsx' | 'csv' | 'pdf' | null {
  const minusculo = nome.toLowerCase()
  if (minusculo.endsWith('.xlsx') || minusculo.endsWith('.xls')) return 'xlsx'
  if (minusculo.endsWith('.csv')) return 'csv'
  if (minusculo.endsWith('.pdf')) return 'pdf'
  return null
}

function lerBase64(arquivo: File): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader()
    leitor.onload = () => resolver((leitor.result as string).split(',')[1])
    leitor.onerror = () => rejeitar(new Error('Falha ao ler o arquivo.'))
    leitor.readAsDataURL(arquivo)
  })
}

// O supabase-js esconde o corpo do erro atrás de "non-2xx status code"; a mensagem
// real da Edge Function vem no Response guardado em error.context. Extrai o {erro}.
async function mensagemErroFuncao(error: unknown, fallback: string): Promise<string> {
  const contexto = (error as { context?: unknown }).context
  if (contexto instanceof Response) {
    try {
      const corpo = await contexto.clone().json()
      if (corpo && typeof corpo.erro === 'string') return corpo.erro
    } catch {
      // corpo não-JSON: ignora e cai no fallback
    }
  }
  return error instanceof Error ? error.message : fallback
}

export default function ImportarArquivo() {
  const [etapa, setEtapa] = useState<Etapa>('escolha')
  // Lazy: le a URL so na abertura (atalho da fornecedora, spec 27/09).
  const [searchParams] = useSearchParams()
  const [tipo, setTipo] = useState<Tipo>(() => preenchimentoDaUrl(searchParams).tipo)
  const [representadaId, setRepresentadaId] = useState(() => preenchimentoDaUrl(searchParams).representadaId)
  const [tabelaNome, setTabelaNome] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [colunas, setColunas] = useState<string[]>([])
  const [linhas, setLinhas] = useState<string[][]>([])
  const [arquivoPath, setArquivoPath] = useState<string | null>(null)
  const [formato, setFormato] = useState<'xlsx' | 'csv' | 'pdf'>('xlsx')
  const [mapa, setMapa] = useState<Record<string, string>>({})
  const [validacao, setValidacao] = useState<ResultadoValidacao | null>(null)
  const [resumo, setResumo] = useState<string>('')
  const [paraRevisar, setParaRevisar] = useState<Array<{ linha: LinhaMapeada; existente: Cliente }>>([])
  // Sempre chaveado por CODIGO: o passo cobra ao mesmo tempo de produto que ja
  // existe e de produto que a planilha vai criar, e so o codigo existe nos dois.
  // Para qual canal cada valor vai no envio quem decide e rotearComissoes.
  const [pendentesComissao, setPendentesComissao] = useState<PendenteComissao[]>([])
  const [comissoesPreenchidas, setComissoesPreenchidas] = useState<Record<string, string>>({})
  const [converterRegra, setConverterRegra] = useState(false)
  // Quem governa a comissao desta tabela. Preenchido so quando a planilha vem
  // sem comissao em NENHUMA linha -- ver politicaComissao.ts.
  const [politica, setPolitica] = useState<Politica | null>(null)
  const [padroes, setPadroes] = useState({ regiao: '', segmento: '', potencial: '' })
  const [divergencias, setDivergencias] = useState<Divergencias | null>(null)
  const [decisoes, setDecisoes] = useState<{ inativar: Set<string>; tirar: Set<string> }>(
    { inativar: new Set(), tirar: new Set() },
  )
  const [precosEditados, setPrecosEditados] = useState<Record<string, string>>({}) // codigo -> preco digitado
  const [agrupamento, setAgrupamento] = useState<Agrupamento | null>(null)
  const [colunasDiferentes, setColunasDiferentes] = useState<number[]>([])
  const [condicaoEscolhida, setCondicaoEscolhida] = useState<number | null>(null)
  // Quais condicoes DESTE arquivo ja viraram tabela. Alimenta a oferta de
  // importar a que sobrou e a trava contra mandar duas condicoes para a mesma
  // tabela (que sobrescreveria os precos recem-importados).
  const [condicoesImportadas, setCondicoesImportadas] =
    useState<Array<{ numero: number; tabelaNome: string }>>([])
  // Planilha com mais de uma aba: a edge para antes de parsear e devolve a lista
  // para a pessoa escolher. Guardamos o File (nao o base64) para o reenvio --
  // no celular, segurar ~5 MB de texto em memoria por causa de um passo de
  // escolha e desperdicio; o navegador ja tem o arquivo.
  const [arquivoPendente, setArquivoPendente] =
    useState<{ arquivo: File; formato: 'xlsx' | 'csv' | 'pdf' } | null>(null)
  const [abas, setAbas] = useState<string[]>([])
  const [abaEscolhida, setAbaEscolhida] = useState<string | null>(null)
  // Caminho devolvido pela PRIMEIRA chamada. Reenviado para a edge nao arquivar
  // a mesma planilha duas vezes no storage.
  const [arquivadoEm, setArquivadoEm] = useState('')

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )
  const tabelas = useLiveQuery(
    () =>
      representadaId
        ? banco.tabelas_preco
            .where('representada_id')
            .equals(representadaId)
            .filter((t) => t.apagado_em === null && t.ativa)
            .sortBy('nome')
        : Promise.resolve<TabelaPreco[]>([]),
    [representadaId],
  )

  // Um caminho so para as DUAS chamadas: a primeira (sem escolha) e o reenvio
  // com a aba escolhida. O que muda entre elas e so o `escolha`.
  async function enviarParaEdge(
    arquivo: File,
    formatoDetectado: 'xlsx' | 'csv' | 'pdf',
    escolha?: { aba?: string; jaArquivadoEm?: string },
  ) {
    setErro(null)
    setCarregando(true)
    try {
      const conteudo = await lerBase64(arquivo)
      const { data, error } = await supabase.functions.invoke('importar-arquivo', {
        body: corpoImportarArquivo(
          { formato: formatoDetectado, nome_arquivo: arquivo.name, conteudo_base64: conteudo },
          escolha,
        ),
      })
      if (error) throw new Error(await mensagemErroFuncao(error, 'Falha na importação.'))
      if (data.erro) throw new Error(data.erro)

      // A planilha tem mais de uma aba e ninguem escolheu ainda: a edge parou
      // ANTES de parsear (e por isso nao estourou a memoria) e devolveu so a
      // lista. Nada foi lido; a leitura de verdade acontece no reenvio.
      const escolhaDeAba = pedeEscolhaDeAba(data)
      if (escolhaDeAba) {
        setArquivoPendente({ arquivo, formato: formatoDetectado })
        setAbas(escolhaDeAba.abas)
        setArquivadoEm(escolhaDeAba.arquivadoEm)
        setAbaEscolhida(null)
        setFormato(formatoDetectado)
        setEtapa('abas')
        return
      }

      setFormato(formatoDetectado)
      setColunas(data.colunas)
      setLinhas(data.linhas)
      setArquivoPath(data.arquivo_path)
      // Alíquotas fiscais não têm perfil salvo (nem sinônimo de auto-mapeamento
      // hoje): é planilha rara e o formato de coluna é o do modelo canônico, ao
      // contrário de produtos/clientes, importados toda semana com cabeçalhos
      // que variam de fornecedora para fornecedora.
      if (tipo === 'aliquotas') {
        setMapa({})
      } else {
        // perfil salvo da representada tem prioridade sobre a sugestao automatica
        const perfil = await banco.perfis_importacao
          .where('representada_id')
          .equals(representadaId)
          .filter((p) => p.apagado_em === null && p.tipo === tipo && p.formato === formatoDetectado)
          .first()
        setMapa(mapaInicial(perfil?.mapa_colunas, data.colunas, tipo))
      }
      setEtapa('mapeamento')
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha na importação.')
    } finally {
      setCarregando(false)
    }
  }

  async function aoEnviarArquivo(arquivo: File) {
    const formatoDetectado = formatoDoNome(arquivo.name)
    if (!formatoDetectado) {
      setErro('Use um arquivo .xlsx, .csv ou .pdf.')
      return
    }
    // Arquivo NOVO: esquece a escolha de aba do anterior. Sem isto, mandar outra
    // planilha logo depois reenviaria o caminho arquivado da primeira.
    setArquivoPendente(null)
    setAbas([])
    setAbaEscolhida(null)
    setArquivadoEm('')
    await enviarParaEdge(arquivo, formatoDetectado)
  }

  // Reenvia o MESMO arquivo com a aba escolhida e o caminho ja arquivado.
  function aoConfirmarAba() {
    if (!arquivoPendente || !abaEscolhida) return
    void enviarParaEdge(arquivoPendente.arquivo, arquivoPendente.formato, {
      aba: abaEscolhida,
      jaArquivadoEm: arquivadoEm,
    })
  }

  async function aoConfirmarMapeamento() {
    // salva/atualiza o perfil de mapeamento da representada -- aliquotas nao
    // tem perfil (ver comentario em enviarParaEdge), entao nao ha o que salvar.
    if (tipo !== 'aliquotas') {
      const existente = await banco.perfis_importacao
        .where('representada_id')
        .equals(representadaId)
        .filter((p) => p.apagado_em === null && p.tipo === tipo && p.formato === formato)
        .first()
      const perfil: PerfilImportacao = {
        ...(existente ?? { ...novoRegistroBase(), representada_id: representadaId, tipo, formato, opcoes: {} }),
        mapa_colunas: mapa,
      }
      await gravarLocalEEnfileirar('perfis_importacao', perfil)
    }

    const mapeadas = aplicarMapa(colunas, linhas, mapa)
    if (tipo === 'produtos') {
      // Codigo repetido = o mesmo produto em outra condicao (preco x comissao).
      // So perguntamos quando ha repeticao: planilha normal segue direto para a
      // previa, sem passo novo nenhum.
      const agrupado = agruparCondicoes(mapeadas)
      if (agrupado.condicoes.length > 1) {
        setAgrupamento(agrupado)
        setColunasDiferentes(colunasQueDiferem(colunas, linhas, agrupado.condicoes))
        setCondicaoEscolhida(null)
        setEtapa('condicoes')
        return
      }
    }
    setValidacao(
      tipo === 'produtos'
        ? validarProdutos(mapeadas)
        : tipo === 'clientes'
          ? validarClientes(mapeadas)
          : validarAliquotas(mapeadas),
    )
    setEtapa('previa')
  }

  function aoConfirmarCondicao() {
    const condicao = agrupamento?.condicoes.find((c) => c.numero === condicaoEscolhida)
    if (!condicao) return
    // A validacao roda so sobre as linhas da condicao escolhida: e ela que vira
    // tabela, e e dela que sai a pendencia de comissao mais adiante.
    setValidacao(validarProdutos(condicao.linhas))
    setEtapa('previa')
  }

  // Recomecar do zero: alem da etapa, zera a memoria do arquivo ANTERIOR. Sem
  // isto, `condicoesImportadas` sobrevive e a trava do nome de tabela -- que so
  // faz sentido ENTRE condicoes do mesmo arquivo -- passa a barrar uma tabela
  // que nao tem nada a ver com o arquivo novo, e o campo extra de nome aparece
  // numa importacao que ja pediu o nome no passo inicial.
  function novaImportacao() {
    setAgrupamento(null)
    setColunasDiferentes([])
    setCondicaoEscolhida(null)
    setCondicoesImportadas([])
    // idem para a aba: o arquivo anterior nao tem nada a ver com o proximo
    setArquivoPendente(null)
    setAbas([])
    setAbaEscolhida(null)
    setArquivadoEm('')
    setEtapa('escolha')
  }

  // Volta para a escolha da condicao reaproveitando o arquivo que JA esta no
  // estado (colunas, linhas, mapa, arquivo_path). E o que dispensa procurar o
  // arquivo de novo. Nao volta para a etapa 'escolha' de proposito: ali apareceria
  // o seletor de arquivo, sugerindo um envio que nao e necessario.
  function retomarOutraCondicao() {
    setValidacao(null)
    setDivergencias(null)
    setDecisoes({ inativar: new Set(), tirar: new Set() })
    setPrecosEditados({})
    setPendentesComissao([])
    setComissoesPreenchidas({})
    setConverterRegra(false)
    setResumo('')
    setErro(null)
    setTabelaNome('') // obrigatorio digitar outro: a tabela anterior ja tem a outra condicao
    const restantes = (agrupamento?.condicoes ?? []).filter(
      (c) => !condicoesImportadas.some((j) => j.numero === c.numero),
    )
    setCondicaoEscolhida(restantes.length === 1 ? restantes[0].numero : null)
    setEtapa('condicoes')
  }

  // Depois das divergencias (ou no lugar delas, quando nao ha), verifica se a
  // tabela de destino ficaria com produto vendavel sem comissao. Ver B1 da spec.
  //
  // Quem DECIDE e passoDeComissaoDaImportacao, funcao pura e testada. Esta
  // funcao so busca o estado e obedece. Enquanto a decisao morava aqui, ela
  // conhecia um dos tres estados ruins e os outros dois aplicavam calados --
  // ver o comentario de passoComissao.ts.
  async function irParaComissoesOuAplicar(
    decisoesAplicar: { inativar: Set<string>; tirar: Set<string> },
    precosAplicar: Record<string, string>,
    // `pularPolitica` volta aqui DEPOIS de ela responder quem governa a tabela.
    // Sem isso a pergunta renasceria a cada passagem e ela nunca sairia dali.
    opcoes: { pularPolitica?: boolean } = {},
  ) {
    if (tipo !== 'produtos') {
      void aoAplicar(decisoesAplicar, precosAplicar)
      return
    }
    const tab = (tabelas ?? []).find(
      (t) => normalizarTexto(t.nome) === normalizarTexto(tabelaNome),
    )
    const regras = await banco.regras_comissao
      .where('representada_id').equals(representadaId)
      .filter((r) => r.apagado_em === null)
      .toArray()
    // Mesma escolha que a VENDA fara depois (motor.ts): override da tabela,
    // senao a padrao da marca. Tabela que ainda nao existe so pode herdar a
    // padrao -- e e exatamente o que escolherRegra devolve para null.
    const regra = escolherRegra(regras, tab?.id ?? null)
    const produtoPorCodigo = new Map(
      (
        await banco.produtos
          .where('representada_id').equals(representadaId)
          .filter((p) => p.apagado_em === null)
          .toArray()
      ).map((p) => [p.codigo, p]),
    )
    const linhasDaPlanilha = linhasParaComissao(
      validacao?.validas ?? [], precosAplicar, produtoPorCodigo,
    )

    // ANTES de perguntar QUEM esta descoberto, perguntar QUEM GOVERNA a tabela.
    // So acontece quando a planilha nao traz comissao em nenhuma linha -- ver
    // politicaComissao.ts, que decide sozinha se ha o que perguntar.
    if (!opcoes.pularPolitica) {
      const daTabela = regraPropriaDaTabela(regras, tab?.id)
      const daFornecedora = regraPadraoDaFornecedora(regras)
      const pctsDe = async (idDaRegra: string | undefined) =>
        idDaRegra
          ? await banco.comissao_percentuais
              .where('regra_id').equals(idDaRegra)
              .filter((pc) => pc.apagado_em === null)
              .toArray()
          : []
      const escolha = politicaDaTabela({
        regraDaTabela: daTabela,
        regraDaFornecedora: daFornecedora,
        percentuaisDaTabela: await pctsDe(daTabela?.id),
        percentuaisDaFornecedora: await pctsDe(daFornecedora?.id),
        produtosComPrecoNaTabela: tab ? await produtosComPrecoNaTabela(representadaId, tab.id) : [],
        linhas: linhasDaPlanilha,
      })
      if (escolha.perguntar) {
        setPolitica(escolha)
        setConverterRegra(false)
        setPendentesComissao([])
        setComissoesPreenchidas({})
        setDecisoes(decisoesAplicar)
        setPrecosEditados(precosAplicar)
        setEtapa('politica')
        return
      }
    }
    // Converter a regra e decisao DELA, e muda o que fica pendente: por isso a
    // pergunta vem antes da conta, e a regra convertida entra na conta.
    const planilhaTemComissao = (validacao?.validas ?? []).some((l) => l.campos.comissao?.trim())
    let converter = false
    if (regra && regra.tipo === 'pct_fixo' && planilhaTemComissao) {
      converter = await confirmar(
        [
          `A tabela "${tabelaNome.trim()}" hoje paga ${regra.pct_padrao ?? 0}% fixo em todos os produtos.`,
          'A planilha traz comissões por produto. Mudar esta tabela para comissão por produto?',
          'Cancelar mantém o % fixo e ignora a coluna de comissão da planilha.',
        ].join('\n\n'),
      )
    }
    setConverterRegra(converter)
    const regraEfetiva =
      converter && regra ? { ...regra, tipo: 'pct_por_item' as const } : regra

    const percentuais = regraEfetiva
      ? await banco.comissao_percentuais
          .where('regra_id').equals(regraEfetiva.id)
          .filter((p) => p.apagado_em === null)
          .toArray()
      : []
    // O que JA esta gravado com preco. Com regra, o universo e o que a regra
    // governa (a padrao abrange varias tabelas); sem regra nenhuma, o universo
    // e a tabela de destino. Tabela que ainda nao existe nao tem produto algum.
    const jaPrecificados = regraEfetiva
      ? await produtosComPrecoNaRegra(representadaId, regraEfetiva)
      : tab
        ? await produtosComPrecoNaTabela(representadaId, tab.id)
        : []
    const passo = passoDeComissaoDaImportacao(
      regraEfetiva,
      percentuais,
      jaPrecificados,
      linhasDaPlanilha,
    )

    if (passo.acao === 'avisar_regra_vazia') {
      // AVISA, nao trava -- decisao dela em 26/08: "algumas fornecedoras so
      // irao colocar % da comissao na tabela". Fornecedora com % vazio e
      // legitimo quando a TABELA tem regra propria; nesse caso escolherRegra ja
      // prefere a da tabela e este aviso nem nasce. Ele so aparece quando a
      // regra que de fato GOVERNA esta tabela e um % fixo em branco.
      //
      // Os DOIS remedios sao nomeados a pedido dela. Percentual por produto e
      // inerte numa pct_fixo, entao 'definir por produto' exige antes trocar o
      // TIPO da regra -- por isso a frase diz TROCAR, e nao so definir.
      // As aspas citam os rotulos EXATOS do seletor de CardRegra.tsx, para ela
      // achar na tela o que a mensagem mandou procurar. Ha guarda para isso.
      const seguir = await confirmar(
        [
          `A comissão que vale para "${tabelaNome.trim()}" é um % fixo, e ele está EM BRANCO.`,
          'Esta importação também não traz comissão por produto. Do jeito que está, tudo o que for vendido por esta tabela paga 0% — sem aviso, na hora da venda.',
          'Na tela da Fornecedora você resolve de dois jeitos: preencher o "% fixo sobre o total", que vale para todos os produtos; ou trocar a regra para "% por produto/linha" e definir produto a produto.',
          'Cancele para resolver antes. OK importa assim mesmo.',
        ].join('\n\n'),
      )
      if (!seguir) return
      void aoAplicar(decisoesAplicar, precosAplicar)
      return
    }
    if (passo.acao === 'aplicar') {
      void aoAplicar(decisoesAplicar, precosAplicar)
      return
    }
    setPendentesComissao(passo.pendentes)
    setComissoesPreenchidas({})
    setDecisoes(decisoesAplicar)
    setPrecosEditados(precosAplicar)
    setEtapa('comissoes')
  }

  // Ela escolheu que a tabela passa a seguir a fornecedora.
  //
  // Duas gravacoes, nesta ordem, e as duas pelo outbox (offline-first):
  //  1. o % digitado, quando houve, vira a regra PADRAO da fornecedora;
  //  2. a regra propria da tabela deixa de governar.
  //
  // ⚠ A regra da tabela e APAGADA (soft delete), nao destruida: os percentuais
  // continuam presos a ela no servidor, entao da para recuperar mexendo no
  // banco. A tela avisa que ela nao desfaz isso sozinha -- e isso e verdade.
  async function seguirPoliticaDaFornecedora(pctDigitado: string | null) {
    try {
      const regras = await banco.regras_comissao
        .where('representada_id').equals(representadaId)
        .filter((r) => r.apagado_em === null)
        .toArray()
      const tab = (tabelas ?? []).find(
        (t) => normalizarTexto(t.nome) === normalizarTexto(tabelaNome),
      )
      const daFornecedora = regraPadraoDaFornecedora(regras)

      if (pctDigitado !== null) {
        const pct = converterComissao(pctDigitado)
        // Falhar alto: gravar um pct invalido aqui poria a fornecedora inteira
        // a pagar 0% -- o proprio defeito que este passo existe para evitar.
        if (pct === null) throw new Error(`Percentual inválido: "${pctDigitado}".`)
        await gravarLocalEEnfileirar(
          'regras_comissao',
          daFornecedora
            ? { ...daFornecedora, tipo: 'pct_fixo' as const, pct_padrao: pct }
            : {
                ...novoRegistroBase(),
                representada_id: representadaId,
                tabela_preco_id: null,
                tipo: 'pct_fixo' as const,
                pct_padrao: pct,
                gatilho_liberacao: 'faturamento' as const,
              },
        )
      }

      const daTabela = regraPropriaDaTabela(regras, tab?.id)
      if (daTabela) await apagarLocalEEnfileirar('regras_comissao', daTabela.id)

      setPolitica(null)
      void aoAplicar(decisoes, precosEditados)
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao gravar a comissão.')
    }
  }

  async function prepararAplicacao() {
    if (!validacao) return
    try {
      const tab = (tabelas ?? []).find(
        (t) => normalizarTexto(t.nome) === normalizarTexto(tabelaNome),
      )
      if (tipo === 'produtos') {
        // ⚠ DUAS PERGUNTAS DIFERENTES, E SO UMA DEPENDE DE HISTORICO.
        // Comparar com o que ja existia exige tabela anterior -- sem ela,
        // `calcularDivergencias` marcaria TODOS os produtos como `novos` e a
        // primeira importacao abriria uma tela com 500 nomes. Ja conferir se os
        // precos e o fator batem entre si nao depende de historico: a planilha
        // fala sozinha. Ate 29/08 as duas viajavam juntas atras da mesma trava,
        // e o catalogo NOVO -- que e justamente o que traz as duas colunas de
        // preco -- era o unico caso sem guarda nenhuma.
        let d: Divergencias
        if (tab) {
          const precos = await banco.precos.where('tabela_preco_id').equals(tab.id)
            .filter((p) => p.apagado_em === null).toArray()
          const porId = new Map(
            (await banco.produtos.where('representada_id').equals(representadaId)
              .filter((p) => p.apagado_em === null).toArray()).map((p) => [p.id, p]),
          )
          const naTabela = precos
            .map((p) => porId.get(p.produto_id))
            .filter((p): p is NonNullable<typeof p> => !!p)
            .map((p) => ({ produto_id: p.id, codigo: p.codigo, descricao: p.descricao }))
          d = calcularDivergencias(validacao.validas, naTabela)
        } else {
          d = divergenciasSemHistorico(validacao.validas)
        }
        if (precisaAbrirDivergencias(d)) {
          setDivergencias(d)
          setDecisoes({ inativar: new Set(), tirar: new Set() })
          setPrecosEditados({})
          setEtapa('divergencias')
          return
        }
      }
      void irParaComissoesOuAplicar({ inativar: new Set(), tirar: new Set() }, {})
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao preparar a aplicação.')
    }
  }

  async function aoAplicar(
    decisoesAplicar: { inativar: Set<string>; tirar: Set<string> } = { inativar: new Set(), tirar: new Set() },
    precosAplicar: Record<string, string> = {},
    comissoesAplicar: Record<string, string> = {},
  ) {
    if (!validacao) return
    setEtapa('aplicando')
    setErro(null)
    try {
      if (tipo === 'produtos') {
        // Cada percentual digitado no passo vai por UM canal so. Produto que
        // esta na planilha viaja na propria linha (a edge mapeia por codigo);
        // produto que ficou de fora dela viaja por produto_id. Mandar pelos
        // dois grava o percentual duas vezes.
        const codigosDaPlanilha = new Set(validacao.validas.map((l) => l.campos.codigo))
        const rota = rotearComissoes(pendentesComissao, comissoesAplicar, codigosDaPlanilha)
        const payload = validacao.validas.map((l) => {
          const editado = precosAplicar[l.campos.codigo]?.trim()
          const precoTexto = editado ? editado : l.campos.preco
          return {
            codigo: l.campos.codigo,
            descricao: l.campos.descricao,
            // Cadastro e fiscais: so viajam as chaves das colunas MAPEADAS (R13
            // estendida, 15/10) -- coluna fora da planilha nao apaga o que esta
            // gravado. A regra mora em `camposProduto` (testavel).
            ...camposProduto(l.campos),
            preco: precoTexto ? (converterNumeroBR(precoTexto) || null) : null,
            // O que veio na planilha sempre manda; abaixo dele, o que ela
            // digitou no passo para um codigo QUE ESTA nesta planilha.
            comissao: l.campos.comissao
              ? converterComissao(l.campos.comissao)
              : converterComissao((rota.porCodigo[l.campos.codigo] ?? '').trim()),
            // A regra de quantidade viaja junto com o PRECO: as duas vao para
            // a mesma linha de `precos`, entao separa-las aqui so criaria a
            // chance de uma chegar sem a outra.
            //
            // `converterQuantidadeRegra` devolve `undefined` para campo vazio
            // (o normal) e `null` para valor sujo; o `?? null` achata os dois
            // no null da coluna anulavel. Achatar e SEGURO aqui, e nao
            // descuido: este map roda sobre `validacao.validas`, e linha com
            // valor sujo ja foi reprovada por validarProdutos antes de chegar
            // aqui -- o `null` de "invalido" nao existe neste ponto do fluxo.
            quantidade_minima: converterQuantidadeRegra(l.campos.quantidade_minima ?? '') ?? null,
            quantidade_maxima: converterQuantidadeRegra(l.campos.quantidade_maxima ?? '') ?? null,
            // A caixa fechada viaja PARTIDA em dois lados, e a edge e quem
            // separa: o fator e do PRODUTO (a caixa e a mesma em toda tabela) e
            // o avulso e do PRECO (a mesma fornecedora pode ter uma tabela que
            // vende avulso e outra que nao). Mas os dois saem da MESMA linha da
            // planilha, e pelo mesmo motivo da quantidade da regra logo acima:
            // separa-los aqui so criaria a chance de um chegar sem o outro.
            //
            // ⭐ Espalhados, e nao escritos a mao, por causa da R13: coluna nao
            // mapeada nao emite a chave, e chave que nao viaja e o que diz a
            // edge "ninguem tem opiniao, nao mexa". A decisao mora em
            // `camposCaixaFechada` porque este map nao e testavel aqui.
            ...camposCaixaFechada(l.campos),
          }
        })
        const { data, error } = await supabase.functions.invoke('aplicar-importacao', {
          body: {
            representada_id: representadaId,
            tabela_preco_nome: tabelaNome.trim() || null,
            arquivo_path: arquivoPath,
            perfil_id: null,
            erros_previa: validacao.erros,
            linhas: payload,
            inativar: [...decisoesAplicar.inativar],
            tirar_da_tabela: [...decisoesAplicar.tirar],
            // So o que rotearComissoes mandou por aqui: produto que ja existe
            // no banco e NAO esta nesta planilha. O resto ja subiu na linha.
            comissoes_existentes: rota.existentes
              .map(({ produto_id, texto }) => ({ produto_id, pct: converterComissao(texto.trim()) }))
              .filter((c): c is { produto_id: string; pct: number } => c.pct !== null),
            converter_regra_para_por_item: converterRegra,
          },
        })
        if (error) throw new Error(await mensagemErroFuncao(error, 'Falha ao aplicar.'))
        if (data.erro) throw new Error(data.erro)
        await sincronizar()
        const comissaoMapeada = Object.values(mapa).includes('comissao')
        // Conta o que ficou REALMENTE sem comissao: descarta o que a usuaria
        // acabou de preencher no passo e, de quebra, para de contar linha sem
        // preco -- essa nunca pagou 0%, porque nao e vendavel pela tabela.
        const semComissao = comissaoMapeada
          ? linhasParaComissao(validacao.validas, precosAplicar, new Map()).filter(
              (l) =>
                l.temPreco &&
                !l.temComissao &&
                converterComissao((comissoesAplicar[l.codigo] ?? '').trim()) === null,
            ).length
          : 0
        setResumo(
          `${data.inseridos} produtos novos, ${data.atualizados} atualizados, ${data.pulados} pulados.` +
            (semComissao > 0 ? ` ${semComissao} sem comissão (0% até definir na tela da Fornecedora).` : ''),
        )
        if (condicaoEscolhida !== null) {
          setCondicoesImportadas((atual) => [
            ...atual,
            { numero: condicaoEscolhida, tabelaNome: tabelaNome.trim() },
          ])
        }
      } else if (tipo === 'aliquotas') {
        // Qual das 3 abas esta planilha e, pela coluna de valor que foi
        // mapeada -- mesma leitura que validarAliquotas ja fez linha a linha
        // (aqui so precisa de UMA, porque o mapeamento e o mesmo para o
        // arquivo inteiro). So um dos tres arrays abaixo vem preenchido; a
        // edge grava so nas tabelas cujo array chegou com linhas.
        const tipoDeAliquota = detectarTipoDeAliquota(validacao.validas[0]?.campos ?? {})
        const { data, error } = await supabase.functions.invoke('aplicar-importacao-fiscal', {
          body: {
            representada_id: representadaId,
            arquivo_path: arquivoPath,
            linhas_icms: tipoDeAliquota === 'icms'
              ? validacao.validas.map((l) => paraLinhaIcms(l.campos))
              : [],
            linhas_st: tipoDeAliquota === 'st'
              ? validacao.validas.map((l) => paraLinhaSt(l.campos))
              : [],
            linhas_desconto_frete: tipoDeAliquota === 'desconto_frete'
              ? validacao.validas.map((l) => paraLinhaDescontoFrete(l.campos))
              : [],
          },
        })
        if (error) throw new Error(await mensagemErroFuncao(error, 'Falha ao aplicar.'))
        if (data.erro) throw new Error(data.erro)
        await sincronizar()
        const total = (data.gravados_icms ?? 0) + (data.gravados_st ?? 0) + (data.gravados_desconto_frete ?? 0)
        const ignoradas = data.ignoradas ?? 0
        setResumo(
          `${total} ${total === 1 ? 'alíquota gravada' : 'alíquotas gravadas'}.` +
            (ignoradas > 0
              ? ` ${ignoradas} ${ignoradas === 1 ? 'linha ignorada' : 'linhas ignoradas'} (produto não encontrado nesta fornecedora).`
              : ''),
        )
      } else {
        const resultado = await aplicarClientes(validacao.validas, representadaId, {
          regiao: padroes.regiao || undefined,
          segmento: padroes.segmento || undefined,
          potencial: (padroes.potencial || undefined) as Classificacao | undefined,
        })
        // registra no historico (sobe pela outbox como qualquer escrita local)
        await gravarLocalEEnfileirar('importacoes', {
          ...novoRegistroBase(),
          perfil_id: null,
          arquivo_path: arquivoPath,
          status: 'aplicada',
          total_inseridos: resultado.criados,
          total_atualizados: resultado.atualizados,
          total_pulados: validacao.erros.length,
          erros: validacao.erros,
        })
        setResumo(
          `${resultado.criados} clientes novos, ${resultado.atualizados} atualizados automaticamente.` +
            (resultado.paraRevisar.length > 0
              ? ` ${resultado.paraRevisar.length} possíveis duplicados para revisar.`
              : ''),
        )
        if (resultado.paraRevisar.length > 0) {
          setParaRevisar(resultado.paraRevisar)
          setEtapa('resultado') // a tela de resultado mostra a mesclagem pendente
          return
        }
      }
      setEtapa('resultado')
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao aplicar.')
      setEtapa('previa')
    }
  }

  const estiloCampo = 'w-full border border-slate-300 rounded-lg px-3 py-2'

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Importar</h2>
      <a href="/importacoes" className="text-sm text-teal-700 underline">Ver histórico</a>
      {erro && <CartaoErro titulo="Não foi possível importar">{erro}</CartaoErro>}

      {etapa === 'escolha' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div>
            <label className="block text-sm text-slate-600 mb-1">O que importar?</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value as Tipo)} className={estiloCampo}>
              <option value="produtos">Produtos e preços</option>
              <option value="clientes">Clientes</option>
              <option value="aliquotas">Alíquotas fiscais (ICMS/ST/frete)</option>
            </select>
          </div>
          <a
            href={`/modelos/modelo-${tipo}.xlsx`}
            download
            className="inline-flex items-center gap-1 text-sm text-teal-700 underline"
          >
            📥 Baixar modelo de{' '}
            {tipo === 'produtos' ? 'Produtos e preços' : tipo === 'clientes' ? 'Clientes' : 'Alíquotas fiscais'}
            {' '}(.xlsx)
          </a>
          {tipo === 'aliquotas' && (
            <p className="text-xs text-slate-500">
              O modelo tem 3 abas — ICMS, ST e DESCONTO_FRETE. Importe uma aba por vez: envie o
              arquivo, escolha a aba, e repita para as outras.
            </p>
          )}
          <div>
            <label className="block text-sm text-slate-600 mb-1" htmlFor="representada">Fornecedora</label>
            <select
              id="representada"
              value={representadaId}
              // Trocar de fornecedora LIMPA o nome da tabela ja digitado. Sem
              // isto o nome sobrevivia a troca e passava a ser comparado com as
              // tabelas da fornecedora NOVA: ou casava com uma delas e a
              // importacao atualizava a tabela errada, ou criava ali uma tabela
              // que ela nunca quis. O nome de tabela so tem sentido dentro de
              // uma fornecedora.
              onChange={(e) => {
                setRepresentadaId(e.target.value)
                setTabelaNome('')
              }}
              className={estiloCampo}
            >
              <option value="">Selecione…</option>
              {representadas?.map((r) => (
                <option key={r.id} value={r.id}>{r.nome}</option>
              ))}
            </select>
          </div>
          {tipo === 'produtos' && representadaId && (
            <div>
              <CampoNomeDeTabela
                id="tabela-nome"
                rotulo="Tabela de preço (preços e comissão)"
                valor={tabelaNome}
                aoMudar={setTabelaNome}
                existentes={tabelas?.map((t) => t.nome)}
              />
            </div>
          )}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Arquivo</label>
            <label
              className={`flex flex-col items-center justify-center gap-1 w-full rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
                !representadaId || carregando
                  ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                  : 'border-teal-500 bg-teal-50 text-teal-700 cursor-pointer hover:bg-teal-100 active:bg-teal-100'
              }`}
            >
              <span className="text-4xl leading-none" aria-hidden="true">📥</span>
              <span className="text-base font-semibold">
                {carregando ? 'Lendo o arquivo…' : 'Escolher arquivo'}
              </span>
              <span className="text-xs text-slate-500">
                Toque aqui para buscar um .xlsx, .csv ou .pdf
              </span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.pdf"
                disabled={!representadaId || carregando}
                onChange={(e) => {
                  const arquivo = e.target.files?.[0]
                  if (arquivo) void aoEnviarArquivo(arquivo)
                }}
                className="sr-only"
              />
            </label>
            {!representadaId && (
              <p className="text-xs text-amber-600 mt-1">Selecione uma fornecedora antes de enviar o arquivo.</p>
            )}
            <p className="text-xs text-slate-400 mt-1">
              PDF: até ~5 MB e ~500 linhas (leitura por IA, requer internet). Planilhas grandes: use xlsx/csv.
            </p>
          </div>
        </div>
      )}

      {etapa === 'abas' && arquivoPendente && (
        <EscolhaAba
          nomeArquivo={arquivoPendente.arquivo.name}
          abas={abas}
          escolhida={abaEscolhida}
          carregando={carregando}
          titulo={tipo === 'aliquotas' ? 'De qual aba eu leio as alíquotas?' : undefined}
          descricao={
            tipo === 'aliquotas'
              ? `«${arquivoPendente.arquivo.name}» tem ${abas.length} abas. Toque na que você quer importar agora: ICMS, ST ou DESCONTO_FRETE.`
              : undefined
          }
          aoEscolher={setAbaEscolhida}
          aoVoltar={novaImportacao}
          aoConfirmar={aoConfirmarAba}
        />
      )}

      {etapa === 'mapeamento' && (
        <>
          <MapeamentoColunas
            tipo={tipo}
            colunas={colunas}
            amostra={linhas[0] ?? []}
            mapa={mapa}
            aoMudar={setMapa}
          />
          <button
            onClick={() => void aoConfirmarMapeamento()}
            disabled={tipo === 'produtos' && !Object.values(mapa).includes('codigo')}
            className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50"
          >
            Continuar
          </button>
          {tipo === 'produtos' && !Object.values(mapa).includes('codigo') && (
            <p className="text-xs text-amber-600">Mapeie ao menos a coluna do código do produto.</p>
          )}
          {/* Saida do mapeamento: quem percebe aqui que errou a fornecedora, o
              nome da tabela ou o proprio arquivo nao tinha como corrigir sem
              recarregar a pagina. Os campos da etapa "escolha" continuam
              preenchidos; so o arquivo precisa ser reenviado. */}
          <button
            type="button"
            onClick={novaImportacao}
            className="w-full text-slate-600 text-sm underline py-2"
          >
            ← Voltar
          </button>
        </>
      )}

      {etapa === 'condicoes' && agrupamento && (
        <EscolhaCondicao
          agrupamento={agrupamento}
          colunas={colunas}
          linhas={linhas}
          colunasDiferentes={colunasDiferentes}
          jaImportadas={condicoesImportadas}
          escolhida={condicaoEscolhida}
          tabelaNome={tabelaNome}
          pedirNomeTabela={condicoesImportadas.length > 0}
          tabelasExistentes={tabelas?.map((t) => t.nome)}
          aoEscolher={setCondicaoEscolhida}
          aoMudarTabela={setTabelaNome}
          aoVoltar={() => setEtapa('mapeamento')}
          aoConfirmar={aoConfirmarCondicao}
        />
      )}

      {etapa === 'previa' && validacao && (
        <>
          <PreviaImportacao
            resultado={validacao}
            camposVisiveis={[...new Set(Object.values(mapa))]}
            // So conta quando a coluna de comissao foi mapeada: sem ela, TODAS
            // as linhas seriam "sem comissao" e o aviso amarelo logo abaixo ja
            // diz o que importa.
            semComissao={
              tipo === 'produtos' && Object.values(mapa).includes('comissao')
                ? linhasParaComissao(validacao.validas, precosEditados, new Map()).filter(
                    (l) => l.temPreco && !l.temComissao,
                  ).length
                : 0
            }
          />
          {tipo === 'produtos' && !Object.values(mapa).includes('comissao') && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
              Você não mapeou a comissão — os produtos ficarão a 0% nesta tabela até você definir na tela da Fornecedora.
            </p>
          )}
          {tipo === 'produtos' && Object.values(mapa).includes('preco') && !tabelaNome.trim() && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
              Informe o nome da tabela de preço — a planilha tem coluna de preço e ele é obrigatório.
            </p>
          )}
          {tipo === 'clientes' && (
            <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-slate-600 mb-1">Região (em lote)</label>
                <input value={padroes.regiao} onChange={(e) => setPadroes((p) => ({ ...p, regiao: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">Segmento (em lote)</label>
                <input value={padroes.segmento} onChange={(e) => setPadroes((p) => ({ ...p, segmento: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">Classificação (em lote)</label>
                <select value={padroes.potencial} onChange={(e) => setPadroes((p) => ({ ...p, potencial: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2">
                  <option value="">—</option>
                  {CLASSIFICACOES.map((c) => (
                    <option key={c} value={c}>{ROTULO_CLASSIFICACAO[c]}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <button
            onClick={() => void prepararAplicacao()}
            disabled={
              validacao.validas.length === 0 ||
              (tipo === 'produtos' && Object.values(mapa).includes('preco') && !tabelaNome.trim())
            }
            className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50"
          >
            Aplicar importação ({validacao.validas.length}{' '}
            {validacao.validas.length === 1 ? 'linha' : 'linhas'})
          </button>
          {/* Sem isto a previa e um beco sem saida: marcar a coluna errada
              reprovava todas as linhas e a unica saida era recarregar a pagina
              e subir o arquivo de novo. colunas/linhas/mapa continuam no
              estado, entao voltar so re-renderiza o passo anterior. */}
          <button
            type="button"
            onClick={() => setEtapa('mapeamento')}
            className="w-full text-slate-600 text-sm underline py-2"
          >
            ← Voltar ao mapeamento
          </button>
        </>
      )}

      {etapa === 'divergencias' && divergencias && (
        <TelaDivergencias
          divergencias={divergencias}
          nomeTabela={tabelaNome.trim()}
          inativar={decisoes.inativar}
          tirar={decisoes.tirar}
          precosEditados={precosEditados}
          aoMudar={setDecisoes}
          aoEditarPreco={(codigo, valor) => setPrecosEditados((atual) => ({ ...atual, [codigo]: valor }))}
          aoConfirmar={() => void irParaComissoesOuAplicar(decisoes, precosEditados)}
        />
      )}

      {etapa === 'politica' && politica?.perguntar && (
        <EscolhaPoliticaComissao
          nomeTabela={tabelaNome.trim()}
          nomeFornecedora={representadas?.find((r) => r.id === representadaId)?.nome ?? 'a fornecedora'}
          jaTemComissaoPropria={politica.jaTemComissaoPropria}
          produtosDaTabela={politica.produtosDaTabela}
          pctDaFornecedora={politica.pctDaFornecedora}
          podeDefinirPctDaFornecedora={politica.podeDefinirPctDaFornecedora}
          aoSeguirFornecedora={(pct) => void seguirPoliticaDaFornecedora(pct)}
          aoDecidirNaTabela={() => {
            setPolitica(null)
            void irParaComissoesOuAplicar(decisoes, precosEditados, { pularPolitica: true })
          }}
        />
      )}

      {etapa === 'comissoes' && (
        <PreenchimentoComissao
          pendentes={pendentesComissao.map((p) => ({ ...p, chave: p.codigo }))}
          nomeTabela={tabelaNome.trim()}
          valores={comissoesPreenchidas}
          aoMudar={(codigo, texto) =>
            setComissoesPreenchidas((atual) => ({ ...atual, [codigo]: texto }))
          }
          aoAplicarATodos={(texto) =>
            setComissoesPreenchidas(
              Object.fromEntries(pendentesComissao.map((p) => [p.codigo, texto])),
            )
          }
          aoConfirmar={() => void aoAplicar(decisoes, precosEditados, comissoesPreenchidas)}
        />
      )}

      {etapa === 'aplicando' && <p className="text-sm text-slate-500">Aplicando…</p>}

      {etapa === 'resultado' && paraRevisar.length > 0 && (
        <MesclagemDuplicados
          pares={paraRevisar}
          representadaId={representadaId}
          aoConcluir={(r) => {
            setParaRevisar([])
            setResumo(
              (anterior) => `${anterior} Revisão: ${r.mesclados} mesclados, ${r.criados} criados, ${r.pulados} pulados.`,
            )
          }}
        />
      )}

      {/* Ordem desta tela, aprendida no smoke de 2026-07-28: primeiro o que
          aconteceu, depois o que FALTA fazer, e so entao a saida. Antes o "Nova
          importacao" ficava dentro da caixa verde, ACIMA da oferta -- ela saiu
          por ele e subiu o arquivo de novo, que e justamente o que a feature
          existe para evitar. */}
      {etapa === 'resultado' && (() => {
        const restantes = (agrupamento?.condicoes ?? []).filter(
          (c) => !condicoesImportadas.some((j) => j.numero === c.numero),
        )
        const temOferta = condicoesImportadas.length > 0 && restantes.length > 0
        const lista = restantes.map((c) => c.numero).join(' e ')
        return (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
              <h3 className="font-medium text-emerald-800">Importação concluída</h3>
              <p className="text-sm text-emerald-700">{resumo}</p>
              {/* Sem oferta pendente, esta e a unica saida e fica aqui mesmo. */}
              {!temOferta && (
                <button onClick={novaImportacao} className="text-sm text-teal-700 underline">
                  Nova importação
                </button>
              )}
            </div>

            {temOferta && (
              <>
                {/* O arquivo continua no estado da tela: aqui NAO se pede o arquivo de novo. */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <p className="text-sm text-amber-800">
                    Este arquivo ainda tem {restantes.length > 1 ? 'as condições' : 'a condição'}{' '}
                    {lista}, que não {restantes.length > 1 ? 'foram importadas' : 'foi importada'}.
                  </p>
                  <button
                    type="button"
                    onClick={retomarOutraCondicao}
                    className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium"
                  >
                    Importar a condição {lista} agora
                  </button>
                  <p className="text-xs text-amber-700">
                    Se você sair desta tela, precisará subir o arquivo de novo.
                  </p>
                </div>
                <button
                  onClick={novaImportacao}
                  className="w-full text-slate-500 text-sm underline py-2"
                >
                  Nova importação (deixar {restantes.length > 1 ? 'as condições' : 'a condição'}{' '}
                  {lista} de fora)
                </button>
              </>
            )}
          </>
        )
      })()}
    </section>
  )
}
